import { useMemo } from 'react'
import { SiteLinksGroupType } from '@/shared/api/site-links-group/site-links-group.types'

export interface GroupsSiteFormattedRowType {
    id: number
    groupName: string
    linksCount: number
    budget: number
    creationDate: string
    subscribers: number
    reach: number
    users: number
    visits: number
    cpu: number
    cpv: number
    ctr_in_percent: number
    leads: number
    cpl: number
    c_in_percent: number
    cpm: number
}

export const useGroupsSitesFormattedRows = (
    groups: SiteLinksGroupType[]
): GroupsSiteFormattedRowType[] => {
    const formattedRows = useMemo(() => {
        let totalLinksCount = 0
        const rows = groups.map((group): GroupsSiteFormattedRowType => {
            const links = group.siteLinks
            const links_group_id = group.site_links_group_id

            const linksCount = links.length

            const budget = links.reduce(
                (sum, link) => sum + (link.budget || 0),
                0
            )

            totalLinksCount += group.siteLinks.length

            const creationDate = group.created_at
            const subscribers = links.reduce(
                (sum, link) =>
                    sum + (link?.publisher?.telegram_channel_subscribers || 0),
                0
            )
            const reach = links.reduce(
                (sum, link) => sum + (link.publisher?.telegram_post_reach || 0),
                0
            )
            const users = links.reduce(
                (sum, link) => sum + (link.users || 0),
                0
            )
            const visits = links.reduce(
                (sum, link) => sum + (link.visits || 0),
                0
            )

            const cpu = users > 0 ? budget / users : 0

            const cpv = links.reduce((sum, link) => sum + (link.cpv || 0), 0)

            const cpm = reach > 0 ? budget / reach : 0

            const ctr_in_percent = reach > 0 ? (users / reach) * 100 : 0
            const leads = links.reduce(
                (sum, link) => sum + (link.leads || 0),
                0
            )

            const cpl = leads > 0 ? budget / leads : 0

            const c_in_percent = users > 0 ? (leads / users) * 100 : 0

            return {
                id: links_group_id,
                groupName: group.group_name,
                linksCount,
                budget,
                creationDate,
                subscribers,
                reach,
                visits,
                users,
                cpu,
                cpv,
                ctr_in_percent,
                leads,
                cpl,
                c_in_percent,
                cpm,
            }
        })

        const allLinksGroupDb = rows.find(
            (row) => row.groupName === 'Все ссылки'
        )
        if (allLinksGroupDb) {
            allLinksGroupDb.linksCount = totalLinksCount
        }
        const filteredRows = rows.filter(
            (row) => row.groupName !== 'Все ссылки'
        )

        const aggregate = filteredRows.reduce(
            (acc, row) => {
                acc.linksCount += row.linksCount
                acc.budget += row.budget
                acc.subscribers += row.subscribers
                acc.reach += row.reach
                acc.users += row.users
                acc.visits += row.visits
                acc.leads += row.leads
                return acc
            },
            {
                linksCount: 0,
                budget: 0,
                subscribers: 0,
                reach: 0,
                users: 0,
                visits: 0,
                leads: 0,
            }
        )
        const totalUsers = allLinksGroupDb?.users
            ? allLinksGroupDb.users + aggregate.users
            : aggregate.users
        const totalVisits = allLinksGroupDb?.visits
            ? allLinksGroupDb.visits + aggregate.visits
            : aggregate.visits
        const totalLeads = allLinksGroupDb?.leads
            ? allLinksGroupDb.leads + aggregate.leads
            : aggregate.leads
        const totalBudget = allLinksGroupDb?.budget
            ? allLinksGroupDb?.budget + aggregate.budget
            : aggregate.budget
        const totalReach = allLinksGroupDb?.reach
            ? allLinksGroupDb?.reach + aggregate.reach
            : aggregate.reach
        const totalSubscribers = allLinksGroupDb?.subscribers
            ? allLinksGroupDb?.subscribers + aggregate.subscribers
            : aggregate.subscribers

        const cpu = totalUsers > 0 ? totalBudget / totalUsers : 0
        const cpv = totalVisits > 0 ? totalBudget / totalVisits : 0
        const cpl = totalLeads > 0 ? totalBudget / totalLeads : 0
        const cpm = totalReach > 0 ? totalBudget / totalReach : 0
        const c_in_percent =
            totalUsers > 0 ? (totalLeads / totalUsers) * 100 : 0
        const ctr_in_percent =
            totalReach > 0 ? (totalUsers / totalReach) * 100 : 0

        const allLinksGroup: GroupsSiteFormattedRowType = {
            id: allLinksGroupDb?.id || 0,
            groupName: 'Все ссылки',
            linksCount: allLinksGroupDb?.linksCount ?? 0,
            budget: totalBudget,
            creationDate: allLinksGroupDb?.creationDate || '',
            reach: totalReach,
            users: totalUsers,
            leads: totalLeads,
            visits: totalVisits,
            subscribers: totalSubscribers,
            cpu,
            cpv,
            cpm,
            cpl,
            c_in_percent,
            ctr_in_percent,
        }

        return [...(rows.length ? [allLinksGroup] : []), ...filteredRows]
    }, [groups])

    return formattedRows
}
